import axios from 'axios'

// Supplier Assistant knowledgebase (GET /v1/assistant/config). Held in the
// store so the chatbot and the staff Q&A settings page share one copy: when
// staff publish a change, the refreshed config is committed here and an
// already-open chatbot updates immediately.

const endpoint = process.env.VUE_APP_API_URL + '/v1/assistant/config/'

const state = {
  config: null,
  loading: false,
  // Staff-side taxonomy (groups + categories) shared by the Q&A and Missed
  // Questions settings pages. null = not loaded yet (or last load failed).
  groups: null,
  categories: null,
  optionsLoading: false,
  optionsError: false
}

const getters = {
  assistantConfig (state) {
    return state.config
  },
  assistantOptions (state) {
    return {
      groups: state.groups,
      categories: state.categories,
      loading: state.optionsLoading,
      error: state.optionsError
    }
  }
}

const mutations = {
  setAssistantConfig (state, payload) {
    state.config = payload
  },
  setAssistantConfigLoading (state, payload) {
    state.loading = payload
  },
  setAssistantOptions (state, payload) {
    state.groups = payload.groups
    state.categories = payload.categories
  },
  setAssistantOptionsLoading (state, payload) {
    state.optionsLoading = payload
  },
  setAssistantOptionsError (state, payload) {
    state.optionsError = payload
  }
}

const actions = {
  // Lazy: fetches once on first call (e.g. when the chatbot becomes visible);
  // later calls are no-ops. Errors are swallowed — with no config the chatbot
  // simply doesn't render its launcher.
  loadAssistantConfig ({ state, commit }) {
    if (state.config !== null || state.loading) return Promise.resolve()
    commit('setAssistantConfigLoading', true)
    return axios.get(endpoint, { silent: true }).then(response => {
      commit('setAssistantConfig', response.data)
    }).catch(() => {}).finally(() => {
      commit('setAssistantConfigLoading', false)
    })
  },
  // Groups + categories for the staff Q&A forms and filters. Cached for the
  // session once loaded; after a failure `groups` stays null, so dispatching
  // again (the pages' Retry link) refetches.
  loadAssistantOptions ({ state, commit }) {
    if (state.optionsLoading || state.groups !== null) return Promise.resolve()
    commit('setAssistantOptionsLoading', true)
    commit('setAssistantOptionsError', false)
    const params = { limit: 250, order_by: 'position' }
    const groups = axios.get(process.env.VUE_APP_API_URL + '/v1/assistant/groups/', { params, silent: true })
    const categories = axios.get(process.env.VUE_APP_API_URL + '/v1/assistant/categories/', { params, silent: true })
    return Promise.all([groups, categories]).then(([groupsResponse, categoriesResponse]) => {
      commit('setAssistantOptions', {
        groups: groupsResponse.data.results,
        categories: categoriesResponse.data.results
      })
    }).catch(() => {
      commit('setAssistantOptionsError', true)
    }).finally(() => {
      commit('setAssistantOptionsLoading', false)
    })
  },
  // Always hits the server (the endpoint is served with Cache-Control
  // max-age=300, so bust the browser cache) and replaces the stored config.
  // Used by the Q&A settings page after every create/edit/delete. On failure
  // the previous config is kept.
  refreshAssistantConfig ({ commit }) {
    return axios.get(endpoint, { params: { t: Date.now() }, silent: true }).then(response => {
      commit('setAssistantConfig', response.data)
    }).catch(() => {})
  }
}

export default {
  state, getters, mutations, actions
}
